import React from "react";
import {DamageCarrier, Monster, MonsterDefensiveAbility, MonsterOffensiveAbility} from "../../../server/monster";

interface Props {
    monster: Monster;
    carriers: DamageCarrier[];
}

const border = "1px solid #000";
const th: React.CSSProperties = {border, padding: "3px 5px", fontWeight: "bold", fontSize: 11, verticalAlign: "middle"};
const td: React.CSSProperties = {border, padding: "3px 5px", fontSize: 11, verticalAlign: "top"};

export function MonsterManualEntry({monster, carriers}: Props) {
    const attrs = monster.attributes ?? {};
    const offAbilities = monster.offensiveAbilities ?? [];
    const defAbilities = monster.defensiveAbilities ?? [];

    const seenIds = new Set<number>();
    const monstrous = [
        ...(monster.type?.species?.monstrousAbilities ?? []),
        ...(monster.type?.monstrousAbilities ?? []),
        ...(monster.monstrousAbilities ?? []),
    ].filter(e => {
        const id = e.ability.id as number;
        if (seenIds.has(id)) return false;
        seenIds.add(id);
        return true;
    });

    const typeName = monster.type?.name ?? "";
    const speciesName = monster.type?.species?.name ?? "";
    const carrierName = carriers.find(c => c.id === attrs.carrierId)?.name ?? "";
    const damageLabel = attrs.damage != null
        ? `${attrs.damage}${carrierName ? ` ${carrierName}` : ""}`
        : "";
    const intelligence = monster.intelligence;
    const typeLabel = [speciesName, typeName].filter(Boolean).join(" - ");

    const typeOrder: Record<string, number> = {WEAKNESS: 0, STRENGTH: 1, IMMUNITY: 2};
    const sortedMonstrous = [...monstrous].sort((a, b) =>
        (typeOrder[a.ability.type] ?? 3) - (typeOrder[b.ability.type] ?? 3)
    );

    // ── Icon helpers ─────────────────────────────────────────────────────────

    function OffIcon({label, children}: {label: string; children: React.ReactNode}) {
        return (
            <svg aria-label={label} width="13" height="13" viewBox="0 0 10 10"
                 style={{display: "inline-block", verticalAlign: "middle", marginRight: 2, flexShrink: 0}}>
                {children}
            </svg>
        );
    }

    function BombIcon() {
        return <OffIcon label="Packet Only">
            <circle cx="5" cy="6.5" r="3.5" fill="black"/>
            <line x1="5" y1="3" x2="8" y2="0.5" stroke="black" strokeWidth="1.2" strokeLinecap="round"/>
            <circle cx="8.5" cy="0.5" r="0.8" fill="black"/>
        </OffIcon>;
    }

    function HandIcon() {
        return <OffIcon label="Touchcast">
            <rect x="1.5" y="2" width="1.2" height="4" rx="0.6" fill="black"/>
            <rect x="3.2" y="1" width="1.2" height="4.5" rx="0.6" fill="black"/>
            <rect x="4.9" y="1" width="1.2" height="4.5" rx="0.6" fill="black"/>
            <rect x="6.6" y="2" width="1.2" height="4" rx="0.6" fill="black"/>
            <rect x="1.5" y="5" width="6.3" height="4" rx="1" fill="black"/>
        </OffIcon>;
    }

    function PersonIcon() {
        return <OffIcon label="Selfcast">
            <circle cx="5" cy="2.5" r="2" fill="black"/>
            <path d="M1.5,10 Q1.5,5.5 5,5.5 Q8.5,5.5 8.5,10 Z" fill="black"/>
        </OffIcon>;
    }

    function SwordIcon() {
        return <OffIcon label="Strike">
            <line x1="2" y1="9" x2="8.5" y2="1" stroke="black" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="1.5" y1="5.5" x2="5" y2="8.5" stroke="black" strokeWidth="1.2" strokeLinecap="round"/>
        </OffIcon>;
    }

    function EyeIcon() {
        return <OffIcon label="Gaze">
            <path d="M1,5 Q5,0.5 9,5 Q5,9.5 1,5 Z" fill="black"/>
            <circle cx="5" cy="5" r="2" fill="white"/>
            <circle cx="5" cy="5" r="1.2" fill="black"/>
        </OffIcon>;
    }

    function OffensiveIcons({o}: {o: MonsterOffensiveAbility}) {
        return <>
            {o.packet_only && <BombIcon/>}
            {o.touchcast && <HandIcon/>}
            {o.selfcast && <PersonIcon/>}
            {o.strike && <SwordIcon/>}
            {o.gaze && <EyeIcon/>}
        </>;
    }

    // ── Format helpers ────────────────────────────────────────────────────────

    function formatDefCall(d: MonsterDefensiveAbility): string {
        let text = d.ability.name;
        if (d.swarm != null) text = `SWARM ${d.swarm}: ${text}`;
        if (d.triggered_by) text = `${text} triggered by ${d.triggered_by}`;
        if (d.conditional_on) text = `${text} Conditional on: ${d.conditional_on}`;
        return text;
    }

    function formatOffCall(o: MonsterOffensiveAbility): string {
        let call = o.ability.call.replace(/<carrier>/gi, carrierName);
        if (o.swarm != null) call = `SWARM ${o.swarm}: ${call}`;
        if (o.triggered_by) call = `${call} triggered by ${o.triggered_by}`;
        if (o.conditional_on) call = `${call} Conditional on: ${o.conditional_on}`;
        return call;
    }

    // ── Page 1 ────────────────────────────────────────────────────────────────
    // Fixed at 10in (8.5x11 page with 0.5in margins top+bottom).
    // overflow:hidden guarantees it never spills onto a second sheet.

    const page1 = (
        <div style={{
            height: "10in",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            fontFamily: "Arial, sans-serif",
            pageBreakAfter: "always",
        }}>
            {/* Monster name */}
            <h1 style={{
                textAlign: "center",
                fontSize: 30,
                fontWeight: "bold",
                margin: "0 0 4px 0",
                lineHeight: 1.2,
            }}>
                {monster.name}
            </h1>

            {/* Species - Type */}
            {typeLabel && (
                <p style={{
                    textAlign: "center",
                    fontSize: 15,
                    fontStyle: "italic",
                    margin: "0 0 4px 0",
                    lineHeight: 1.2,
                }}>
                    {typeLabel}
                </p>
            )}

            {/* ACE */}
            {attrs.ace && (
                <p style={{
                    textAlign: "center",
                    fontSize: 18,
                    fontWeight: "bold",
                    margin: "0 0 10px 0",
                }}>
                    ACE — {attrs.ace}
                </p>
            )}

            {/* Image */}
            <div style={{textAlign: "center", flexShrink: 0, marginBottom: 12}}>
                <img
                    src={attrs.image_url || "/monster-placeholder.png"}
                    alt={monster.name}
                    style={{
                        maxHeight: "4in",
                        maxWidth: "55%",
                        objectFit: "contain",
                    }}
                />
            </div>

            {/* Two-column: Costuming | Behavior */}
            <div style={{
                display: "flex",
                flex: 1,
                gap: 0,
                overflow: "hidden",
                alignItems: "flex-start",
                borderTop: border,
            }}>
                {/* Left: Costuming */}
                <div style={{flex: 1, overflow: "hidden", padding: "6px 10px 6px 0"}}>
                    <p style={{
                        fontWeight: "bold",
                        fontSize: 16,
                        margin: "0 0 6px 0",
                        textAlign: "center",
                    }}>
                        Costuming
                    </p>
                    <p style={{
                        fontSize: 11,
                        margin: 0,
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.4,
                    }}>
                        {attrs.physical_appearance ?? ""}
                    </p>
                </div>

                {/* Vertical divider */}
                <div style={{width: 1, background: "#000", alignSelf: "stretch", flexShrink: 0}}/>

                {/* Right: Behavior */}
                <div style={{flex: 1, overflow: "hidden", padding: "6px 0 6px 10px"}}>
                    <p style={{
                        fontWeight: "bold",
                        fontSize: 16,
                        margin: "0 0 6px 0",
                        textAlign: "center",
                    }}>
                        Behavior
                    </p>
                    {attrs.social_behavior && <>
                        <p style={{fontWeight: "bold", fontSize: 12, margin: "0 0 2px 0"}}>Social:</p>
                        <p style={{fontSize: 11, margin: "0 0 10px 0", whiteSpace: "pre-wrap", lineHeight: 1.4}}>
                            {attrs.social_behavior}
                        </p>
                    </>}
                    {attrs.combat_behavior && <>
                        <p style={{fontWeight: "bold", fontSize: 12, margin: "0 0 2px 0"}}>Combat:</p>
                        <p style={{fontSize: 11, margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.4}}>
                            {attrs.combat_behavior}
                        </p>
                    </>}
                </div>
            </div>
        </div>
    );

    // ── Page 2+ ───────────────────────────────────────────────────────────────
    // No fixed height — content flows freely across as many pages as needed.

    const page2 = (
        <div style={{fontFamily: "Arial, sans-serif", fontSize: 11}}>
            {/* Name / ACE header row */}
            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: 6,
            }}>
                <span style={{fontSize: 20, fontWeight: "bold"}}>
                    {monster.name}
                    {typeLabel && (
                        <span style={{fontSize: 13, fontWeight: "normal", marginLeft: 10}}>
                            {typeLabel}
                        </span>
                    )}
                </span>
                {attrs.ace && (
                    <span style={{fontSize: 14, fontWeight: "bold"}}>ACE — {attrs.ace}</span>
                )}
            </div>

            {/* Stats table */}
            <table style={{width: "100%", borderCollapse: "collapse", marginBottom: 4}}>
                <colgroup>
                    <col style={{width: "20%"}}/>
                    <col style={{width: "20%"}}/>
                    <col style={{width: "60%"}}/>
                </colgroup>
                <tbody>
                <tr>
                    <td style={th}>Armor</td>
                    <td style={th}>Body</td>
                    <td style={th}>Base Damage</td>
                </tr>
                <tr>
                    <td style={{...td, textAlign: "center", fontSize: 14}}>{attrs.armor ?? ""}</td>
                    <td style={{...td, textAlign: "center", fontSize: 14}}>{attrs.body ?? ""}</td>
                    <td style={{...td, textAlign: "center", fontSize: 13}}>{damageLabel}</td>
                </tr>
                </tbody>
            </table>

            {/* Weapons & Intelligence as plain text */}
            {attrs.weaponry && (
                <p style={{fontSize: 11, margin: "4px 0 2px 0"}}>
                    <strong>Weapons:</strong> {attrs.weaponry}
                </p>
            )}
            {intelligence?.name && (
                <p style={{fontSize: 11, margin: "2px 0 10px 0"}}>
                    <strong>Intelligence:</strong> {intelligence.name}
                </p>
            )}

            {/* Offensives */}
            <p style={{fontWeight: "bold", fontSize: 13, margin: "10px 0 3px 0"}}>Offensives</p>
            <table style={{width: "100%", borderCollapse: "collapse", marginBottom: 10}}>
                <colgroup>
                    <col style={{width: "67%"}}/>
                    <col style={{width: "16%"}}/>
                    <col style={{width: "17%"}}/>
                </colgroup>
                <tbody>
                <tr>
                    <td style={th}>Offensive Call</td>
                    <td style={{...th, textAlign: "center"}}>Recharge</td>
                    <td style={{...th, textAlign: "center"}}>Times/Ever</td>
                </tr>
                {offAbilities.length === 0
                    ? <tr><td colSpan={3} style={{...td, color: "#666", fontStyle: "italic"}}>None</td></tr>
                    : [...offAbilities].sort((a, b) => a.ability.name.localeCompare(b.ability.name)).map((o, i) => (
                        <tr key={o.id ?? i}>
                            <td style={td}>
                                <OffensiveIcons o={o}/>
                                {formatOffCall(o)}
                            </td>
                            <td style={{...td, textAlign: "center"}}>
                                {o.recharge_time > 0 ? `${o.recharge_time}s` : "—"}
                            </td>
                            <td style={{...td, textAlign: "center"}}>{o.quantity}</td>
                        </tr>
                    ))
                }
                </tbody>
            </table>

            {/* Defensives */}
            <p style={{fontWeight: "bold", fontSize: 13, margin: "10px 0 3px 0"}}>Defensives</p>
            <table style={{width: "100%", borderCollapse: "collapse", marginBottom: 10}}>
                <colgroup>
                    <col style={{width: "67%"}}/>
                    <col style={{width: "16%"}}/>
                    <col style={{width: "17%"}}/>
                </colgroup>
                <tbody>
                <tr>
                    <td style={th}>Defensive Call</td>
                    <td style={{...th, textAlign: "center"}}>Recharge</td>
                    <td style={{...th, textAlign: "center"}}>Times/Ever</td>
                </tr>
                {defAbilities.length === 0
                    ? <tr><td colSpan={3} style={{...td, color: "#666", fontStyle: "italic"}}>None</td></tr>
                    : [...defAbilities].sort((a, b) => a.ability.name.localeCompare(b.ability.name)).map((d, i) => (
                        <tr key={d.id ?? i}>
                            <td style={td}>{formatDefCall(d)}</td>
                            <td style={{...td, textAlign: "center"}}>—</td>
                            <td style={{...td, textAlign: "center"}}>{d.quantity}</td>
                        </tr>
                    ))
                }
                </tbody>
            </table>

            {/* Monstrous Abilities */}
            {sortedMonstrous.length > 0 && <>
                <p style={{fontWeight: "bold", fontSize: 13, margin: "10px 0 3px 0"}}>Monstrous Abilities</p>
                <table style={{width: "100%", borderCollapse: "collapse"}}>
                    <colgroup>
                        <col style={{width: "30%"}}/>
                        <col style={{width: "20%"}}/>
                        <col style={{width: "50%"}}/>
                    </colgroup>
                    <tbody>
                    <tr>
                        <td style={th}>Ability</td>
                        <td style={{...th, textAlign: "center"}}>Type</td>
                        <td style={th}>Call</td>
                    </tr>
                    {sortedMonstrous.map((m, i) => (
                        <tr key={m.ability.id ?? i}>
                            <td style={td}>{m.ability.name}</td>
                            <td style={{...td, textAlign: "center"}}>{m.ability.type}</td>
                            <td style={td}>{m.ability.call}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </>}
        </div>
    );

    return <>
        {page1}
        {page2}
    </>;
}
